import { useEffect, useState } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { Title1, Title2, Body1, Button, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import MyRequests from "./pages/MyRequests";
import Approvals from "./pages/Approvals";
import AdminUsers from "./pages/AdminUsers";
import AdminDelegations from "./pages/AdminDelegations";
import TeamBalances from "./pages/TeamBalances";
import CalendarPage from "./pages/Calendar";

const useStyles = makeStyles({
  root: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: tokens.spacingHorizontalXXL
  },
  // Embed mode strips the outer chrome to maximize the iframe's usable area.
  embedRoot: {
    maxWidth: "100%",
    margin: "0",
    padding: tokens.spacingHorizontalM
  },
  signInScreen: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalXXXL,
    textAlign: "center"
  },
  nav: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`
  },
  navLink: {
    textDecoration: "none",
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold
  }
});

interface ClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

function useUser() {
  const [user, setUser] = useState<ClientPrincipal | null | undefined>(undefined);
  useEffect(() => {
    fetch("/.auth/me")
      .then((r) => r.json())
      .then((d) => setUser(d.clientPrincipal ?? null))
      .catch(() => setUser(null));
  }, []);
  return user;
}

function NotFound() {
  return <Title2>Page not found</Title2>;
}

export default function App() {
  const styles = useStyles();
  const location = useLocation();
  const user = useUser();
  // Hide /approvals from rank-and-file employees — the API enforces this too,
  // but trimming the nav avoids dangling links that 403.
  const canApprove =
    !!user && (user.userRoles.includes("manager") || user.userRoles.includes("admin"));
  const canAdmin = !!user && user.userRoles.includes("admin");

  // /embed/* routes are rendered without the outer nav + sign-in chrome so the
  // SharePoint iframe shows only the calendar grid. The user does NOT need to
  // be authenticated for these — the SWA route table marks them anonymous and
  // the API gates on the calendar_embed_public setting.
  const isEmbed = location.pathname.startsWith("/embed");

  if (isEmbed) {
    return (
      <div className={styles.embedRoot}>
        <Routes>
          <Route path="/embed/calendar" element={<CalendarPage embedMode={true} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    );
  }

  // Anonymous user hitting a non-embed route: SWA's responseOverride redirects
  // 401s on /api/* to /login, which downgrades to AAD-blocked CORS for XHR
  // calls — that lands in fetchWithRetry's onWarming handler and shows the
  // "Waking up the database…" message, which is wrong (the DB is fine; they're
  // just not signed in). Short-circuit here so the page never makes the doomed
  // call and the user sees a clear sign-in prompt instead.
  if (user === null) {
    return (
      <div className={styles.root}>
        <Title1>AdQuire Time Off</Title1>
        <div className={styles.signInScreen}>
          <Body1>Sign in with your AdQuire Microsoft 365 account to continue.</Body1>
          <Button as="a" href="/login" appearance="primary" size="large">
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  // user === undefined: /.auth/me hasn't resolved yet. Show a brief spinner
  // (sub-second on a warm pageload) instead of letting pages fire requests
  // that might race the auth state.
  if (user === undefined) {
    return (
      <div className={styles.root}>
        <Title1>AdQuire Time Off</Title1>
        <div className={styles.signInScreen}>
          <Spinner size="medium" label="Loading…" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Title1>AdQuire Time Off</Title1>
      <nav className={styles.nav}>
        <Link to="/" className={styles.navLink}>Calendar</Link>
        <Link to="/requests" className={styles.navLink}>My requests</Link>
        {canApprove && <Link to="/approvals" className={styles.navLink}>Approvals</Link>}
        {canApprove && <Link to="/team" className={styles.navLink}>Team</Link>}
        {canAdmin && <Link to="/admin" className={styles.navLink}>Admin</Link>}
        {canAdmin && <Link to="/admin/delegations" className={styles.navLink}>Delegations</Link>}
        <span style={{ marginLeft: "auto" }}>
          {user === undefined && <Spinner size="tiny" />}
          {user === null && <Button as="a" href="/login" appearance="primary">Sign in</Button>}
          {user && (
            <>
              <Body1>{user.userDetails}</Body1>
              {" · "}
              <a href="/logout">Sign out</a>
            </>
          )}
        </span>
      </nav>

      <Routes>
        <Route path="/" element={<CalendarPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/requests" element={<MyRequests />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/admin" element={<AdminUsers />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/delegations" element={<AdminDelegations />} />
        <Route path="/team" element={<TeamBalances />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}
